/* =========================================================================
 * SUN BELT · CAA · MAC — from each conference's own CHAMPIONSHIPS SCHEDULE.
 *
 * Brian: "keep working down the conference lists and keep it going."
 *
 * ⚠️ THIS IS THE BATCH THAT PREVIOUSLY PRODUCED NOTHING, and the difference is
 * the SOURCE, not more effort. These sites are JavaScript SPAs — a plain fetch
 * of sunbeltsports.org or caasports.com returns an empty template shell rather
 * than a 404, so research against them looks successful while silently falling
 * back to Wikipedia. That is exactly what happened on the first attempt.
 *
 * The way through is that each conference publishes a static ARTICLE page —
 * "2026-27 Championships Schedule" — which a fetch CAN read. It is also the
 * best source available, better than a sponsored-sports list: what a conference
 * SCHEDULES A CHAMPIONSHIP FOR is a direct statement that the championship
 * exists, and it prints the date and site alongside. A sports page only tells
 * you a sport is played, which is what makes Patriot League wrestling (EIWA)
 * and Big West men's water polo (MPSF) look like conference titles.
 *
 * ⚠️ EVERY RECORD CARRIES ITS PUBLISHED DATE. That is most of the value here:
 * ~2,580 records on this object sit in NO DATE, invisible to Stages, Worklist
 * and the Calendar — the three views a rep uses to decide what to work.
 *
 * The date is the OPENING day, never the last. A countdown exists so a rep is
 * ready BEFORE the championship starts; keyed to the final day it reads "3 days
 * out" when the event is already in its third day. Eleven American Conference
 * records were wrong in exactly that way and have been corrected.
 *
 * ⚠️ NO REGULAR-SEASON TITLES ARE CREATED HERE, and that is a finding rather
 * than an omission. The Sun Belt's own schedule table carries "Regular-Season
 * Champion" and "Tournament Champion" as COLUMN HEADERS — so it plainly
 * recognizes both — but those columns were UNPOPULATED on the page. A header is
 * evidence the conference tracks a thing; it is not evidence of which sports
 * award it. Creating sixteen regular-season titles off a blank column would be
 * inventing championships from a table's shape.
 * ========================================================================= */

const SRC = {
  sun: 'https://sunbeltsports.org/sports/2026/7/27/26_27_championship_schedule.aspx',
  caa: 'https://caasports.com/sports/2026/6/15/2026-27CAAChampionships.aspx',
  mac: 'https://getsomemaction.com/',
}

const D = (code, conference, sport, gender, date, source, note = '') => ({
  st: code, state: '', body: conference, sport, gender,
  divisions: ['Championship'],
  source,
  segment: 'Conference', level: 'Conference',
  season: '2027',
  activity: false,
  date,
  note,
})

/* ---- SUN BELT CONFERENCE — 2026-27, sixteen championships -------------- */
const SUN = [
  D('SBC', 'Sun Belt Conference', 'Cross Country', 'Men', '2026-10-30', SRC.sun),
  D('SBC', 'Sun Belt Conference', 'Cross Country', 'Women', '2026-10-30', SRC.sun),
  D('SBC', 'Sun Belt Conference', 'Soccer', 'Men', '2026-11-08', SRC.sun, 'Campus sites.'),
  D('SBC', 'Sun Belt Conference', 'Soccer', 'Women', '2026-11-09', SRC.sun),
  D('SBC', 'Sun Belt Conference', 'Volleyball', 'Women', '2026-11-19', SRC.sun),
  D('SBC', 'Sun Belt Conference', 'Football', 'Men', '2026-12-04', SRC.sun, 'Championship game at a campus site — the higher seed hosts.'),
  D('SBC', 'Sun Belt Conference', 'Indoor Track & Field', 'Men', '2027-02-22', SRC.sun),
  D('SBC', 'Sun Belt Conference', 'Indoor Track & Field', 'Women', '2027-02-22', SRC.sun),
  D('SBC', 'Sun Belt Conference', 'Basketball', 'Men', '2027-03-02', SRC.sun),
  D('SBC', 'Sun Belt Conference', 'Basketball', 'Women', '2027-03-02', SRC.sun),
  D('SBC', 'Sun Belt Conference', 'Golf', 'Women', '2027-04-19', SRC.sun),
  D('SBC', 'Sun Belt Conference', 'Tennis', 'Men', '2027-04-22', SRC.sun),
  D('SBC', 'Sun Belt Conference', 'Golf', 'Men', '2027-04-26', SRC.sun),
  D('SBC', 'Sun Belt Conference', 'Tennis', 'Women', '2027-04-28', SRC.sun),
  D('SBC', 'Sun Belt Conference', 'Beach Volleyball', 'Women', '2027-04-29', SRC.sun),
  D('SBC', 'Sun Belt Conference', 'Softball', 'Women', '2027-05-12', SRC.sun),
  D('SBC', 'Sun Belt Conference', 'Outdoor Track & Field', 'Men', '2027-05-13', SRC.sun),
  D('SBC', 'Sun Belt Conference', 'Outdoor Track & Field', 'Women', '2027-05-13', SRC.sun),
  D('SBC', 'Sun Belt Conference', 'Baseball', 'Men', '2027-05-25', SRC.sun),
]

/* ---- COASTAL ATHLETIC ASSOCIATION — 2026-27 ---------------------------
 * ⚠️ NO FOOTBALL, and its absence is the point. CAA Football is a SEPARATE
 * football-only NCAA conference with its own, larger membership, and it does
 * not appear anywhere on the all-sports schedule. An all-sports CAA football
 * championship would be a phantom — the same shape as MVC football (which is
 * the MVFC) and Atlantic 10 football (which BECAME CAA Football in 2007). */
const CAA = [
  D('CAA', 'Coastal Athletic Association', 'Cross Country', 'Men', '2026-10-30', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Cross Country', 'Women', '2026-10-30', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Field Hockey', 'Women', '2026-11-04', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Soccer', 'Men', '2026-11-05', SRC.caa, 'Early rounds at the higher seeds; final at the highest remaining seed.'),
  D('CAA', 'Coastal Athletic Association', 'Soccer', 'Women', '2026-11-05', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Volleyball', 'Women', '2026-11-19', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Indoor Track & Field', 'Men', '2027-02-24', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Indoor Track & Field', 'Women', '2027-02-24', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Swimming & Diving', 'Men', '2027-02-24', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Swimming & Diving', 'Women', '2027-02-24', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Basketball', 'Men', '2027-03-05', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Basketball', 'Women', '2027-03-10', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Golf', 'Women', '2027-04-16', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Tennis', 'Men', '2027-04-23', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Tennis', 'Women', '2027-04-23', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Golf', 'Men', '2027-04-25', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Lacrosse', 'Men', '2027-05-06', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Lacrosse', 'Women', '2027-05-06', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Softball', 'Women', '2027-05-12', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Outdoor Track & Field', 'Men', '2027-05-14', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Outdoor Track & Field', 'Women', '2027-05-14', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Rowing', 'Women', '2027-05-14', SRC.caa),
  D('CAA', 'Coastal Athletic Association', 'Baseball', 'Men', '2027-05-26', SRC.caa),
]

/* ---- MID-AMERICAN CONFERENCE — DELIBERATELY PARTIAL --------------------
 * ⚠️ ONLY FIVE, AND THE SHORTFALL IS HONEST. The MAC publishes no consolidated
 * 2026-27 championships schedule; these five come from individual press
 * releases. Its own tournaments archive still ends at 2025-26.
 *
 * The MAC near-certainly also crowns cross country, men's soccer, wrestling,
 * swimming & diving, gymnastics, indoor and outdoor track, golf, tennis,
 * baseball, softball, women's lacrosse and women's rowing — but "near-certainly"
 * from a historical pattern is an INFERENCE, and inferring a championship is
 * how a rep ends up working a title that does not exist. They are a named gap,
 * revisited when the MAC publishes its schedule.
 *
 * Also noted for later: the MAC's archive shows a separate FALL INDIVIDUAL
 * TENNIS championship distinct from the spring team event — real precedent, no
 * confirmed 2026-27 date, so not recorded. */
const MAC = [
  D('MAC', 'Mid-American Conference', 'Field Hockey', 'Women', '2026-11-04', SRC.mac),
  D('MAC', 'Mid-American Conference', 'Volleyball', 'Women', '2026-11-20', SRC.mac, 'Top six teams.'),
  D('MAC', 'Mid-American Conference', 'Football', 'Men', '2026-12-05', SRC.mac, 'Championship game at Ford Field, Detroit — the top two teams in the final standings, no bracket.'),
  D('MAC', 'Mid-American Conference', 'Basketball', 'Men', '2027-03-10', SRC.mac),
  D('MAC', 'Mid-American Conference', 'Basketball', 'Women', '2027-03-10', SRC.mac),
]

export const GAPS_D1 = [...SUN, ...CAA, ...MAC]
